import { withEntityReferrer } from 'pages/documents/common/utils';
import { documentsConfig } from 'pages/map-query/datasets/documents.config';
import { shortLabel } from 'pages/map-query/filter-modal/facet-label';
import type {
  DatasetConfig,
  FilterFieldConfig,
  MultiSelectFilterField,
} from 'pages/map-query/types';

/**
 * One of Map Query's own documents filters, so both pages send the same params.
 * @param {string} id The filter id.
 * @param {string} type The filter's control type.
 * @return {FilterFieldConfig} The filter.
 */
function mapQueryFilter<T extends FilterFieldConfig['type']>(
  id: string,
  type: T
): Extract<FilterFieldConfig, { type: T }> {
  const field = documentsConfig.filters.find(
    (filter): filter is Extract<FilterFieldConfig, { type: T }> =>
      filter.id === id && filter.type === type
  );
  if (!field) throw new Error(`Map Query has no ${type} filter "${id}"`);
  return field;
}

interface Placement {
  label: string;
  category: string;
  helperText?: string;
}

/**
 * The "Filter …" placeholder a rail control reads.
 * @param {Placement} placement The filter's label and section.
 * @return {string} The placeholder.
 */
const placeholderFor = ({ label }: Placement) =>
  `Filter ${label.toLowerCase()}`;

/**
 * A filter as the rail shows it: under one of its sections, with its label and
 * the "Filter …" placeholder its control reads.
 * @param {FilterFieldConfig} field The filter.
 * @param {Placement} placement Its label, section and any explanation.
 * @return {FilterFieldConfig} The filter for the rail.
 */
function inSection<F extends FilterFieldConfig>(
  field: F,
  placement: Placement
): F {
  return { ...field, ...placement, placeholder: placeholderFor(placement) };
}

/**
 * A facet filter offered as the rail's searchable multi-select.
 * @param {object} facet The filter's id, param, facet and option handling.
 * @param {Placement} placement Its label and section.
 * @return {MultiSelectFilterField} The multi-select filter.
 */
function multiSelect(
  facet: Pick<
    MultiSelectFilterField,
    'id' | 'param' | 'facetKey' | 'optionLabel' | 'numericOrder'
  >,
  placement: Placement
): MultiSelectFilterField {
  return {
    ...facet,
    type: 'multi-select',
    ...placement,
    placeholder: placeholderFor(placement),
  };
}

const DATE = 'Date Filters';
const DOCUMENT = 'Document Filters';
const LOCATION = 'Location Filters';
const PARTY = 'Party & Entity Filters';

const PARTY_HELP =
  'Entities affiliated with the transacting parties on the documents.';

const stateCounty = mapQueryFilter('state_county', 'tree-table');
const documentTypes = mapQueryFilter('document_types', 'multi-select-list');

const ENTITY_FILTERS: FilterFieldConfig[] = [
  inSection(mapQueryFilter('file_date', 'date-range'), {
    label: 'File date',
    category: DATE,
  }),
  inSection(mapQueryFilter('recorded_date', 'date-range'), {
    label: 'Recorded date',
    category: DATE,
  }),
  inSection(mapQueryFilter('effective_date', 'date-range'), {
    label: 'Effective date',
    category: DATE,
  }),
  inSection(mapQueryFilter('instrument_date', 'date-range'), {
    label: 'Instrument date',
    category: DATE,
  }),
  multiSelect(
    {
      id: documentTypes.id,
      param: documentTypes.param,
      facetKey: documentTypes.facetKey,
    },
    { label: 'Document Type', category: DOCUMENT }
  ),
  multiSelect(
    {
      id: 'state',
      param: stateCounty.param,
      facetKey: stateCounty.facetKey,
      optionLabel: shortLabel,
    },
    { label: 'State', category: LOCATION }
  ),
  multiSelect(
    {
      id: 'county',
      param: stateCounty.childParam,
      facetKey: stateCounty.childFacetKey,
    },
    { label: 'County', category: LOCATION }
  ),
  multiSelect(
    {
      id: 'reference_location_ids',
      param: 'reference_location_ids',
      facetKey: 'referenceLocation',
    },
    { label: 'Applicable areas', category: LOCATION }
  ),
  inSection(mapQueryFilter('conveyor', 'multi-select-text-search-combo'), {
    label: 'Transacting Grantor',
    category: PARTY,
    helperText: PARTY_HELP,
  }),
  inSection(mapQueryFilter('conveyee', 'multi-select-text-search-combo'), {
    label: 'Transacting Grantee',
    category: PARTY,
    helperText: PARTY_HELP,
  }),
  multiSelect(
    { id: 'entity_roles', param: 'entity_roles', facetKey: 'entityRole' },
    { label: 'Role', category: PARTY }
  ),
  multiSelect(
    {
      id: 'entity_address_ids',
      param: 'entity_address_ids',
      facetKey: 'entityAddress',
      numericOrder: true,
    },
    { label: 'Address', category: PARTY }
  ),
];

/**
 * The entity profile's Documents tab: Map Query's documents runsheet scoped to one
 * entity, every document type included and the entity's duplicate-flagged
 * documents kept in. No map, so no tiles, colour-by, map point or workspace
 * creation.
 * @param {number} entityId The entity whose documents are listed.
 * @return {DatasetConfig} The tab's dataset.
 */
export const entityDocumentsConfig = (entityId: number): DatasetConfig => ({
  id: 'entity-documents',
  label: 'Documents',
  searchEndpoint: documentsConfig.searchEndpoint,
  directHref: documentsConfig.directHref,
  resultsLayout: 'cards',
  highlightRowKey: 'id',
  facetsMode: 'separate',
  // Every facet, as Applicable areas reads one the lean set skips. The field
  // sort spares scoring every text match.
  facetsPayload: { sortAttribute: 'created_at', sortDirection: 'ASC' },
  showSearchTips: false,
  showSortBy: true,
  showSearchError: true,
  documentLink: withEntityReferrer(`/entities/${entityId}`),
  alphabeticalFacetOptions: true,
  filters: ENTITY_FILTERS,
  columns: [],
  defaultPayload: {
    ...documentsConfig.defaultPayload,
    entity_id: entityId,
    include_duplicates: true,
    sortAttribute: 'date',
    sortDirection: 'DESC',
    response_config: { include_facets: false, include_bounding_box: false },
  },
});
